import {
  convertFromRaw, // genKey,
} from 'draft-js'
import escape from 'lodash/escape'
import get from 'lodash/get'
import type { BusinessData } from 'types/business'
import type {
  ButtonElement,
  Campaign,
  CampaignData,
  CampaignFooter,
  CampaignSection,
  CampaignSectionColumn,
  DividerElement,
  EmailElement,
  FooterElement,
  ImageElement,
  PlaylistElement,
  TextElement,
} from 'types/content-editor'
import {
  DEFAULT_FONT_FAMILY,
  DEFAULT_LINE_HEIGHT,
} from '@disco/constants/content-editor'
import { isDark } from '@disco/utils/color-utils'
import { getCampaignTrackArtwork, getTrackSubheader } from '@disco/utils/tracks'
import {
  getFontFamily,
  getFontSize,
  getHeaderSize,
  getImageSrc,
  getSectionPaddings,
} from './editor-utils'
import exportToMjml from './mjml-export'
type TextParams = {
  fontFamily: string
  fontSize: string
  horizonatalPadding: number
  align: string
  color: string
  lineHeight: number
}

const textBlock = (textParams: TextParams, content: string): string => {
  const { fontFamily, fontSize, horizonatalPadding, align, color, lineHeight } =
    textParams
  return `
            <mj-text font-family="${fontFamily}" font-size="${fontSize}px" padding="0 ${horizonatalPadding}px" align="${align}" color="${color}" line-height="${lineHeight}">
              ${content}
            </mj-text>
      `
}

const getTextElement = (
  element: TextElement,
  _data: CampaignData,
  section: CampaignSection,
  campaign: Campaign,
) => {
  const fontFamily = getFontFamily(section, campaign)
  const fontSize = getFontSize(section, campaign)
  const elementContent = element && element.content
  if (!elementContent) return ''
  const splitContent: any[] = []
  let currentSplit = 0
  elementContent.blocks.forEach((block, index) => {
    // don't include last empty block in the output if previous block is an
    // image or button for better look
    if (
      block &&
      block.entityRanges.length === 0 &&
      (!block.text || block.text === '') &&
      index === elementContent.blocks.length - 1 &&
      elementContent.blocks[index - 1].type === 'atomic'
    ) {
      return
    }

    const textAlign = get(block, 'data.text-align')
    const standaloneBlock = block.type === 'atomic' || !!textAlign
    // analyze if we need to split block into separete element
    if (standaloneBlock && currentSplit < splitContent.length) currentSplit++

    if (currentSplit + 1 > splitContent.length) {
      const props = textAlign
        ? {
            align: textAlign.replace('align-', ''),
          }
        : null
      splitContent.push({
        blocks: [block],
        entityMap: {},
        props,
      })
    } else {
      splitContent[currentSplit]?.blocks.push(block)
    }

    if (block.entityRanges.length > 0) {
      block.entityRanges.forEach(({ key }) => {
        if (splitContent[currentSplit]) {
          splitContent[currentSplit].entityMap[key] =
            elementContent.entityMap[key]
        }
      })
    }

    /*
    if (block.text) {
      const match = block.text.match(/\n+$/)
      if (match) {
        for (let i = 0; i < match.length; i++) {
          const newBlock = {
            key: genKey(),
            type: 'unstyled',
            text: '',
            depth: 0,
            data: {},
            entityRanges: [],
            inlineStyleRanges: [],
          }
          splitContent[currentSplit].blocks.push(newBlock)
        }
      }
    }
    */
    // atomic blocks should stay single
    if (standaloneBlock) currentSplit++
  })
  const { h } = getSectionPaddings(section, campaign)
  const lineHeight =
    section.lineHeight || campaign.lineHeight || DEFAULT_LINE_HEIGHT
  const resultString = splitContent
    .map(content => {
      const convertedString = exportToMjml(convertFromRaw(content || {}), {
        baseFontFamily:
          section.fontFamily || campaign.fontFamily || DEFAULT_FONT_FAMILY,
        baseFontSize: section.fontSize || campaign.fontSize,
        imagePadding: h.toString(10),
        lineHeight,
      })
      const textParams = {
        align: get(content, 'props.align', 'left'),
        fontFamily,
        fontSize,
        horizonatalPadding: h,
        color: section.color || '#000',
        lineHeight,
      }

      if (convertedString.startsWith('<figure>')) {
        const strippedString = convertedString
          .replace('<figure>', '')
          .replace('</figure>', '')

        // figure doesn't contain valid mj-* tag, need to wrap it with mj-text
        if (
          !content ||
          !content.entityMap ||
          Object.keys(content.entityMap).length === 0
        ) {
          return textBlock(textParams, strippedString)
        }

        return `
              ${strippedString}
        `
      }

      // regular text blocks
      return textBlock(textParams, convertedString)
    })
    .join('\r\n')
  return resultString
}

const getPlaylistElement = (
  element: PlaylistElement,
  data: CampaignData,
  section: CampaignSection,
  campaign: Campaign,
) => {
  const { h } = getSectionPaddings(section, campaign)

  if (element.playlist_id && data.playlists) {
    const playlist = data.playlists.find(
      p => p.playlist_id === element.playlist_id,
    )
    const alignment = element.buttonAlignment || 'left'
    const buttonBackgroundColor = element.buttonBackgroundColor || '#0693e3'
    const buttonColor = element.buttonColor || '#fff'
    const fontFamily = getFontFamily(section, campaign)
    const fontSize = getFontSize(section, campaign)
    const headerSize = getHeaderSize(fontSize)
    const header =
      !element.disableHeader && playlist
        ? `<mj-text color="${
            section.color || '#000'
          }" font-family="${fontFamily}" font-size="${headerSize}px" padding="10px ${h}px 0 ${h}px">
      <span style="word-break: break-all">${playlist.name}</span>
    </mj-text>`
        : ''

    if (playlist) {
      return `
      ${header}
      <mj-table font-family="${fontFamily}" font-size="${fontSize}px" cellpadding="5" color="${
        section.color || '#000'
      }" padding="10px ${h}px">
        ${playlist.tracks
          .map((t, index) => {
            const section = playlist.sections.find(s => s.position === index)
            const sectionRow = section
              ? `<tr>
              <td colspan="2" style="padding: 6px 0 0 0;">
                <b>${(section.name ?? '').toUpperCase()}</b>
              </td>
            </tr>`
              : ''
            const artwork = getCampaignTrackArtwork(t)
            const trackRow = `
          <tr>
            <td width="50px" style="line-height: 1">
              <img
                style="border:none;border-radius:0;display:block;font-size:13px;outline:0;text-decoration:none;width:40px;height:40px" width="40" height="40"
                src="${artwork || ''}"
              />
            </td>
            <td>
              <b>${t.name}</b>
              <br/>${getTrackSubheader(t)}
            </td>
          </tr>`
            return sectionRow + trackRow
          })
          .join('')}

      </mj-table>
      <mj-button font-family="${fontFamily}" font-size="${fontSize}px" padding="10px ${h}px 0 ${h}px" align="${alignment}" href="${
        playlist.view_playlist_url
      }" color="${buttonColor}" background-color="${buttonBackgroundColor}" font-weight="600">
        ${element.buttonText}
      </mj-button>`
    }
  }

  return `<mj-text></mj-text>`
}

const getImageElement = (
  element: ImageElement,
  section: CampaignSection,
  campaign: Campaign,
) => {
  const { h } = getSectionPaddings(section, campaign)
  const widthString =
    element.width && element.width !== '100%'
      ? ` width="${element.width}px"`
      : ''
  const linkString = element.link ? ` href="${element.link}"` : ''
  const altString = ` alt="${element.alt || ' '}"` // space is added intentionally here to keep alt attribute in resulting html

  const src = getImageSrc(element)
  return `
          <mj-image padding="0 ${h}px 0 ${h}px" src="${src}"${widthString}${linkString}${altString} />`
}

const socialNetwork = {
  facebook: 'Facebook',
  twitter: 'X',
  soundcloud: 'Soundcloud',
  instagram: 'Instagram',
  spotify: 'Spotify',
  apple: 'Apple Music/iTunes',
  web: 'Website',
  youtube: 'YouTube',
}

const socialLinksToMJ = (
  input: Record<string, any>,
  options: {
    logoColor: 'light' | 'dark'
  },
) => {
  const { ...socialObject } = input
  // const iconColor = '#515151'
  // const display = `display="${Object.keys(socialObject)
  //   .map(key => `${key}:url`) // use actual chosen urls
  //   .join(' ')}"`
  let result = ''

  for (const key in socialObject) {
    // 'x' was renamed to 'twitter' but remained in db https://au2.disco-staging-2.com/api/campaign_footers/39/
    if (key === 'x') continue

    if (socialObject[key] && socialObject[key].trim().length > 0) {
      let suffix = '-d'

      if (options.logoColor === 'light') {
        suffix = '-l'
      }

      const url = `https://static.disco.ac/campaign-editor/footer-r3/${key}${suffix}-96.png`
      result += `<mj-social-element icon-size="48px" href="${socialObject[key]}" src="${url}" alt="${socialNetwork[key]}">
      </mj-social-element>`
    }
  }

  return result
}

const getFooterElement = (
  _element: FooterElement,
  _data: CampaignData,
  businessSettings: BusinessData | null | undefined,
  footer: CampaignFooter | null | undefined,
  section: CampaignSection,
  campaign: Campaign,
) => {
  const textColor = section.color || '#222'
  const fontFamily = getFontFamily(section, campaign)
  const fontSize = getFontSize(section, campaign)
  const { h, v } = getSectionPaddings(section, campaign)
  const smallVerticalPadding = v / 2
  let logoColor: 'light' | 'dark' = 'dark'

  if (isDark(section.backgroundColor || '#fff')) {
    logoColor = 'light'
  }

  const social =
    footer && footer.social_links
      ? `<mj-social mode="horizontal" padding="10px ${h}px">
      ${socialLinksToMJ(footer.social_links, {
        logoColor,
      })}
    </mj-social>`
      : ''
  const elementText =
    footer && footer.footer_text_mjml
      ? `<mj-text padding="${smallVerticalPadding}px ${h}px" font-family="${fontFamily}" font-size="${fontSize}px" color="${textColor}" align="center">
      ${footer && footer.footer_text_mjml ? footer.footer_text_mjml : ''}
    </mj-text>`
      : ''
  const addressText =
    businessSettings && businessSettings.business_address_mjml
      ? `<mj-text padding="${smallVerticalPadding}px ${h}px" font-family="${fontFamily}" font-size="12px" color="${textColor}" align="center">
        ${businessSettings.business_address_mjml}
    </mj-text>`
      : ''
  let banner = ''

  if (!get(businessSettings, 'private_metadata.campaigns_hide_disco_logo')) {
    let suffix = '-d'

    if (logoColor === 'light') {
      suffix = '-l'
    }

    banner = `
      <mj-text align="center" padding="20px">
        <a href="https://disco.ac/?utm_source=customer_io&utm_medium=email_footer&utm_campaign=playlist_share">
          <img alt="DISCO" width="70" height="18" src="https://static.disco.ac/campaign-editor/footer-r3/disco-logo${suffix}-140-36.png" />
        </a>
      </mj-text>
    `
  }

  return `
          <mj-divider border-width="1px" border-style="solid" border-color="lightgrey" padding="10px ${h}px" />
          ${social}
          ${elementText}
          ${addressText}
          <mj-text align="center" font-size="12px" padding="${v}px ${h}px">
            <a style="color: ${textColor};" href="{{unsubscribe_url}}">Unsubscribe</a>
          </mj-text>
          ${banner}
          `
}

const getButtonElement = (
  element: ButtonElement,
  section: CampaignSection,
  campaign: Campaign,
) => {
  const { h } = getSectionPaddings(section, campaign)
  const fontFamily = getFontFamily(section, campaign)
  const fontSize = getFontSize(section, campaign)
  return `<mj-button font-family="${fontFamily}" font-size="${fontSize}px" padding="0 ${h}px" href="${element.link}" color="${element.style.color}" background-color="${element.style.backgroundColor}" font-weight="600">
            ${element.caption}
          </mj-button>`
}

const getDividerElement = (
  element: DividerElement,
  section: CampaignSection,
  campaign: Campaign,
) => {
  const { h } = getSectionPaddings(section, campaign)
  return `<mj-divider padding="10px ${h}px" border-width="${element.borderWidth}" border-color="${element.borderColor}" border-style="${element.borderStyle}" />`
}

const getElement = (
  element: EmailElement,
  section: CampaignSection,
  data: CampaignData,
  businessSettings: BusinessData | null | undefined,
  footer: CampaignFooter | null | undefined,
  campaign: Campaign,
) => {
  if (element.type === 'text')
    return getTextElement(element, data, section, campaign)
  if (element.type === 'image')
    return getImageElement(element, section, campaign)
  if (element.type === 'footer')
    return getFooterElement(
      element,
      data,
      businessSettings,
      footer,
      section,
      campaign,
    )
  if (element.type === 'playlist')
    return getPlaylistElement(element, data, section, campaign)
  if (element.type === 'button')
    return getButtonElement(element, section, campaign)
  if (element.type === 'divider')
    return getDividerElement(element, section, campaign)
  throw new Error(
    `mjml getElement do not know element of type: ${(element as any).type}`,
  )
}

const getColumn = (
  column: CampaignSectionColumn,
  section: CampaignSection,
  data: CampaignData,
  businessSettings: BusinessData | null | undefined,
  footer: CampaignFooter | null | undefined,
  campaign: Campaign,
) => {
  let columnContent = ''

  if (column.columnContent) {
    column.columnContent.forEach(element => {
      columnContent += getElement(
        element,
        section,
        data,
        businessSettings,
        footer,
        campaign,
      )
    })
  }

  const { v } = getSectionPaddings(section, campaign)
  return `
        <mj-column padding="${v}px 0">${columnContent}
        </mj-column>`
}

export function generateMjml(
  campaign: Campaign,
  data: CampaignData,
  businessSettings: BusinessData | null | undefined,
  footer: CampaignFooter | null | undefined,
): string {
  const { generalBackground, sections } = campaign
  let contents = ''
  sections.forEach((section, index) => {
    let sectionColumns = ''
    section.columns.forEach(column => {
      sectionColumns += getColumn(
        column,
        section,
        data,
        businessSettings,
        footer,
        campaign,
      )
    })
    let borderRadiusString = ''
    if (index === 0) borderRadiusString = 'border-radius="5px 5px 0 0"'
    if (index === sections.length - 1)
      borderRadiusString = 'border-radius="0 0 5px 5px"'
    const backgroundColor = section.backgroundColor || '#fff'
    const padding = `0`
    contents += `
      <mj-section ${borderRadiusString} padding="${padding}" background-color="${backgroundColor}">${sectionColumns}
      </mj-section>`
  })
  const viewOnlineColor = isDark(generalBackground || '#fff') ? '#eee' : '#222'
  const preheader = data.preheader?.trim()
  const preview = preheader
    ? `<mj-preview>${escape(preheader)}</mj-preview>`
    : ''
  const result = `<mjml>
  <mj-head>
    ${preview}
    <mj-font name="Roboto" href="https://fonts.googleapis.com/css2?family=Roboto" />

    <mj-attributes>
      <mj-all font-family="Helvetica, Arial, sans-serif" />
    </mj-attributes>

    <mj-style inline="inline">
      .text-link {
        color: #5e93c5;
        word-break: break-word;
        text-decoration: none;
      }

      .outside-link {
        color: ${viewOnlineColor};
        text-decoration: underline;
      }

      .text-paragraph {
        margin: 0;
      }

      .email-body {
        padding: 50px 10px;
      }

      .email-wrapper {
        border-radius: 5px;
        box-shadow: 0 0 3px 2px rgba(0, 0, 0, 0.06);
      }

      h1, h2, h3, h4, h5, h6 {
        line-height: 110%;
      }
    </mj-style>
  </mj-head>
  <mj-body background-color="${
    generalBackground || '#fff'
  }" css-class="email-body">
    <mj-section padding="15px 0" css-class="online-invisible">
      <mj-column padding="0">
        <mj-text align="center" font-size="12px"><a class="outside-link" href="__ONLINE_URL__">View this email in your browser</a></mj-text>
      </mj-column>
    </mj-section>
    <mj-wrapper padding="0" css-class="email-wrapper">
      ${contents}
    </mj-wrapper>
  </mj-body>
</mjml>`
  return result
}
